import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useGRN } from "../Hooks/useGRN";
import toast from "react-hot-toast";
import {
  GRN_MATERIAL_COLUMNS,
  GRN_MATERIAL_LABELS,
  REQUIRED_FIELDS,
  GRNMaterial_Condition,
  GRN_MATERIAL_DEFAULT_VALUES,
  GRN_STATUS_NAMES,
} from "./constant";
import {
  SearchableDropdown,
  GenericTable,
  RoleValidator,
  ProductCategoryFilter,
} from "../../../Components/Common";
import { ArrowLeft, Trash2 } from "lucide-react";
import { useWarehouse } from "../../Inventory/Hooks/useWarehouse";
import { useMRR } from "../../Inventory/Hooks/useMRR";
import { ROLES } from "../../../Constants/Strings";
import { GRN_LIST } from "../../../routes";

const CreateGRN = () => {
  const navigate = useNavigate();
  const currentUser = "Current User";

  const [warehouse, setWarehouse] = useState("");
  const [mrr, setMRR] = useState("");
  const [notes, setNotes] = useState("");
  const [grnMaterials, setGrnMaterials] = useState([]);

  const { fetchWarehouses } = useWarehouse();
  const { fetchAllMRRs } = useMRR();
  const { createGRN,} = useGRN();

  // Remove material by its id (product_id)
  const removeMaterial = (material) => {
    setGrnMaterials((prev) => prev.filter((item) => item.id !== material.id));
  };

  // Add selected product to grnMaterials
  const handleGRNMaterial = (product) => {
    // Prevent duplicate products
    if (grnMaterials.some((m) => m.id === product.id)) return;

    setGrnMaterials((prev) => [
      ...prev,
      {
        ...GRN_MATERIAL_DEFAULT_VALUES,
        id: product.id,
        product_id: product.id,
        product_name: product.product_name,
        specification: product.specification,
        UOM: product.UOM_name,
      },
    ]);
  };

  // Submit GRN
  const handleGRNSubmit = async (statusType) => {
    if (!warehouse) return toast.error(REQUIRED_FIELDS.WAREHOUSE);
    if (!grnMaterials.length) return toast.error(REQUIRED_FIELDS.MATERIALS);

    const formattedMaterials = grnMaterials.map((m) => ({
      product: m.product_id,
      quantity: m.quantity || 1,
      product_condition:
        m.product_condition || GRN_MATERIAL_DEFAULT_VALUES.product_condition,
      comments: m.comments || "",
    }));

    const payload = {
      warehouse,
      mrr,
      notes,
      status: statusType,
      materials: formattedMaterials,
      created_by: currentUser,
      modified_by: currentUser,
    };
    const res = await createGRN(payload);
    if (res) navigate(GRN_LIST);
  };

  // Render table fields
  const renderField = (column, row) => {
    switch (column) {
      case "product_name":
        return (
          <div className="flex flex-col">
            <span className="font-semibold text-gray-900">{row.product_name || "-"}</span>
            <span className="text-xs text-gray-500">{row.specification || "-"}</span>
            <span className="text-xs text-gray-500 mt-0.5">{row.UOM || "-"}</span>
          </div>
        );

      case "product_condition":
        return (
          <select
            value={row.product_condition || "GOOD"}
            onChange={(e) =>
              setGrnMaterials((prev) =>
                prev.map((m) =>
                  m.id === row.id
                    ? { ...m, product_condition: e.target.value }
                    : m
                )
              )
            }
            className="w-full pt-1 h-8 px-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
          >
            {GRNMaterial_Condition.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        );

      case "quantity":
        return (
          <input
            type="number"
            min="1"
            value={row.quantity}
            onChange={(e) =>
              setGrnMaterials((prev) =>
                prev.map((m) =>
                  m.id === row.id
                    ? { ...m, quantity: Number(e.target.value) }
                    : m
                )
              )
            }
            className="w-28 h-8 px-1 text-sm border border-gray-300 rounded"
          />
        );

      case "comments":
        return (
          <input
            type="text"
            value={row.comments || ""}
            onChange={(e) =>
              setGrnMaterials((prev) =>
                prev.map((m) =>
                  m.id === row.id ? { ...m, comments: e.target.value } : m
                )
              )
            }
            placeholder="Enter comments"
            className="w-32 h-6 text-sm border-b-2 border-gray-300"
          />
        );

      case "actions":
        return (
          <RoleValidator
            allowedRoles={[ROLES.SITE_ENGINEER, ROLES.PROJECT_ENGINEER]}
          >
            <button
              onClick={() => removeMaterial(row)}
              className="text-red-500 hover:text-red-700"
              title="Remove"
            >
              <Trash2 size={18} />
            </button>
          </RoleValidator>
        );

      default:
        return row[column] || "-";
    }
  };

  return (
    <div className="min-h-[70vh] p-4">
      <div className="flex items-center gap-4 mb-6">
        <span
          onClick={() => navigate(GRN_LIST)}
          className="cursor-pointer text-blue-600 hover:text-blue-800"
        >
          <ArrowLeft size={20} />
        </span>
        <h2 className="text-xl font-semibold text-gray-800">Create GRN</h2>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* GRN Details */}
        <div className="pr-4 border-r border-gray-400">
          <h3 className="text-lg font-semibold text-gray-800 mb-6 text-center">
            GRN Details
          </h3>
          <div className="mb-4">
            <label className="block text-gray-700 font-medium mb-2">
              Warehouse
            </label>
            <SearchableDropdown
              fetchFunction={(param) =>
                fetchWarehouses({ ...param, is_site: true })
              }
              onSelect={(val) => setWarehouse(val)}
              value={warehouse}
              valueKey="id"
              labelKey="name"
              placeholder="Select warehouse"
            />
          </div>
          <div className="mb-4">
            <label className="block text-gray-700 font-medium mb-2">MRR</label>
            <SearchableDropdown
              fetchFunction={(param) =>
                fetchAllMRRs({ ...param, warehouse: warehouse  })
              }
              onSelect={setMRR}
              value={mrr}
              valueKey="id"
              labelKey="mrr_code"
              placeholder="Select MRR"
              disabled={!warehouse}
            />
          </div>
          <div>
            <label className="block text-gray-700 font-medium mb-2">
              Notes
            </label>
            <textarea
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Write note..."
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none resize-none"
            />
          </div>
        </div>

        {/* GRN Material Table */}
        <div>
          <h3 className="text-lg font-semibold text-gray-800 mb-4 text-center">
            GRN Material Details
          </h3>

          <div className="mb-4 ">
            <label className="block text-gray-700 font-medium mb-2">
              Product
            </label>       
            <ProductCategoryFilter
              onProductSelect={handleGRNMaterial}
            />
          </div>

          <div className="border rounded-lg p-2">
            {grnMaterials.length === 0 ? (
              <div className="text-gray-600 text-center py-10">
                Select a product to see materials
              </div>
            ) : (
              <GenericTable
                tableColumns={GRN_MATERIAL_COLUMNS}
                tableRows={grnMaterials}
                columnLabels={GRN_MATERIAL_LABELS}
                renderField={renderField}
                enableMobileCardView={false}
              />
            )}
          </div>

          <div className="flex justify-end gap-4 mt-6">
            <RoleValidator
              allowedRoles={[ROLES.SITE_ENGINEER, ROLES.PROJECT_ENGINEER]}
            >
              <button
                onClick={() => handleGRNSubmit(GRN_STATUS_NAMES.DRAFT)}
                className="px-4 bg-gray-200 text-gray-800 hover:bg-gray-300 py-2 my-2 rounded-lg"
              >
                Save as Draft
              </button>
            </RoleValidator>

            <RoleValidator
              allowedRoles={[ROLES.SITE_ENGINEER, ROLES.PROJECT_ENGINEER]}
            >
              <button
                onClick={() =>
                  handleGRNSubmit(GRN_STATUS_NAMES.PENDING_FOR_APPROVAL)
                }
                className="px-4 bg-primary hover:bg-blue-700 text-white py-2 my-2 rounded-lg"
              >
                Submit for Approval
              </button>
            </RoleValidator>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateGRN;
